import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconButton } from "@smartsuite/react-ui/lib/components/buttons/IconButton/IconButton";
import { NumberInputField } from "@smartsuite/react-ui/lib/components/forms/NumberInputField/NumberInputField";
import { Tooltip } from "@smartsuite/react-ui/lib/components/help/Tooltip/Tooltip";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import { Icon } from "@smartsuite/react-ui/lib/components/utils/Icon/Icon";
import { GrayColors } from "@smartsuite/types";
import type React from "react";
import { memo, useCallback, useRef } from "react";
import type {
  ColumnAlignment,
  LinkedRecordItem,
  LinkedRecordTableField,
} from "../../canvas/state/types";
import { normalizeLinkedRecordTableFields } from "../../canvas/state/utils";
import { TableColumnAlignment } from "./TableColumnAlignment/TableColumnAlignment";

export interface TableColumnSortableItemProps {
  canRemoveItem?: boolean;
  id: string;
  label: string;
  slug: string;
  selected: LinkedRecordItem;
  onChangeSelected: (modifiedItem: LinkedRecordItem) => void;
}

const TableColumnSortableItemRaw: React.FC<TableColumnSortableItemProps> = ({
  canRemoveItem = true,
  id,
  label,
  selected,
  onChangeSelected,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const [inputSelectState, setInputSelectState] = useSelectState();

  const selectedFields = normalizeLinkedRecordTableFields(selected);
  const fieldSettings = selectedFields.find((field) => {
    return field.field === id;
  }) as LinkedRecordTableField;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleChangeAlignment = useCallback(
    (alignment: ColumnAlignment) => {
      const updatedSettings: LinkedRecordTableField = {
        ...fieldSettings,
        textAlign: alignment,
      };
      if (alignment === "left") {
        delete updatedSettings.textAlign;
      }

      onChangeSelected({
        ...selected,
        field_list: selectedFields.map((field) =>
          field.field === id ? updatedSettings : field,
        ),
      });
    },
    [fieldSettings, id, onChangeSelected, selected, selectedFields],
  );

  const handleChangeWidth = useCallback(
    (value: string) => {
      let newWidth = parseInt(value);
      if (isNaN(newWidth) || !newWidth) {
        newWidth = 1;
      }

      const updatedSettings = { ...fieldSettings, width: newWidth };

      onChangeSelected({
        ...selected,
        field_list: selectedFields.map((field) =>
          field.field === id ? updatedSettings : field,
        ),
      });

      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [fieldSettings, id, onChangeSelected, selected, selectedFields],
  );

  const handleRemove = useCallback(() => {
    const fieldList = normalizeLinkedRecordTableFields(selected);

    onChangeSelected({
      ...selected,
      field_list: fieldList.filter((item) => item.field !== id),
    });
  }, [id, onChangeSelected, selected]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="sidebar-selection-config__item">
        <Icon name="drag" size="M" color={GrayColors["G-4"]} />
        <li className="sidebar-selection-config__item_label">{label}</li>

        <div className="sidebar-selection-config__item_width">
          <Tooltip
            text="Column Width"
            padding={2}
            placements={["top", "bottom"]}
            disabled={inputSelectState === "active"}
            target={
              <NumberInputField
                ref={inputRef}
                onFocus={() => setInputSelectState(true)}
                onBlur={() => setInputSelectState(false)}
                state={inputSelectState ? "active" : undefined}
                showSymbol={false}
                autoHideNavigation={true}
                min={1}
                value={`${fieldSettings?.width}`}
                onChangeValue={handleChangeWidth}
                inputStyles={{ backgroundColor: "white" }}
              />
            }
          ></Tooltip>
        </div>

        <TableColumnAlignment
          value={fieldSettings.textAlign}
          onChange={handleChangeAlignment}
        />

        {canRemoveItem && (
          <IconButton
            className="sidebar-selection-config__item__close"
            name="close"
            size="S"
            ariaLabel="Remove"
            onClick={handleRemove}
          />
        )}
      </div>
    </div>
  );
};

export const TableColumnSortableItem = memo(TableColumnSortableItemRaw);
